import React from 'react';
import { Space, Button, Typography, InputNumber } from 'antd';
import { LoadingOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { SourceEpisode } from '../../../types';

const { Text } = Typography;

interface TimingTabPanelProps {
  episode: SourceEpisode;
  selectedPanel: any;
  selectedPanelIndex: number;
  isPlayingVoice: boolean;
  setIsPlayingVoice: (playing: boolean) => void;
  voiceAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
  editedPanels: any[];
  setEditedPanels: (panels: any[]) => void;
  panelsMetadata: any[];
  setPanelsMetadata: (metadata: any[]) => void;
  setHasChanges: (changes: boolean) => void;
}

const TimingTabPanel: React.FC<TimingTabPanelProps> = ({
  episode,
  selectedPanel,
  selectedPanelIndex,
  isPlayingVoice,
  setIsPlayingVoice,
  voiceAudioRef,
  editedPanels,
  setEditedPanels,
  panelsMetadata,
  setPanelsMetadata,
  setHasChanges,
}) => {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      {/* Listen to Voice */}
      {episode.masterAudioUrl && (
        <Button
          icon={isPlayingVoice ? <LoadingOutlined /> : <CustomerServiceOutlined />}
          onClick={() => {
            if (isPlayingVoice) {
              // Stop
              if (voiceAudioRef.current) {
                voiceAudioRef.current.pause();
                voiceAudioRef.current = null;
              }
              setIsPlayingVoice(false);
            } else {
              // Play from start
              if (voiceAudioRef.current) {
                voiceAudioRef.current.pause();
              }

              const audio = new Audio(episode.masterAudioUrl);
              voiceAudioRef.current = audio;
              audio.currentTime = selectedPanel.startTime;
              setIsPlayingVoice(true);
              audio.play();

              // Use timeupdate event to check and stop at endTime
              const checkTime = () => {
                if (audio.currentTime >= selectedPanel.endTime) {
                  audio.pause();
                  audio.removeEventListener('timeupdate', checkTime);
                  setIsPlayingVoice(false);
                }
              };
              audio.addEventListener('timeupdate', checkTime);

              // Cleanup on audio end or error
              audio.addEventListener('ended', () => {
                audio.removeEventListener('timeupdate', checkTime);
                setIsPlayingVoice(false);
              });
              
              audio.addEventListener('error', () => {
                audio.removeEventListener('timeupdate', checkTime);
                setIsPlayingVoice(false);
              });
            }
          }}
          block
          size="small"
          type={isPlayingVoice ? 'primary' : 'default'}
        >
          {isPlayingVoice ? '⏸ Stop' : '🎧 Listen Voice'} ({(selectedPanel.endTime - selectedPanel.startTime).toFixed(1)}s)
        </Button>
      )}

      {selectedPanel.caption && (
        <div style={{ 
          padding: '8px',
          backgroundColor: '#2a2a2a',
          borderRadius: '4px',
          border: '1px solid #444'
        }}>
          <Text style={{ fontSize: '10px', color: '#999', display: 'block', marginBottom: 4 }}>
            📝 Caption:
          </Text>
          <Text style={{ fontSize: '11px', color: '#fff' }}>
            {selectedPanel.caption}
          </Text>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
            Start (s)
          </Text>
          <InputNumber
            value={selectedPanel.startTime}
            onChange={(value) => {
              if (value !== null && value >= 0) {
                const newPanels = [...editedPanels];
                newPanels[selectedPanelIndex] = {
                  ...newPanels[selectedPanelIndex],
                  startTime: value
                };
                setEditedPanels(newPanels);
                
                // Also update panelsMetadata
                const newMetadata = [...panelsMetadata];
                newMetadata[selectedPanelIndex] = {
                  ...newMetadata[selectedPanelIndex],
                  startTime: value
                };
                setPanelsMetadata(newMetadata);
                
                setHasChanges(true);
              }
            }}
            min={0}
            max={selectedPanel.endTime - 0.1}
            step={0.1}
            size="small"
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
            End (s)
          </Text>
          <InputNumber
            value={selectedPanel.endTime}
            onChange={(value) => {
              if (value !== null && value > selectedPanel.startTime) {
                const newPanels = [...editedPanels];
                newPanels[selectedPanelIndex] = {
                  ...newPanels[selectedPanelIndex],
                  endTime: value
                };
                setEditedPanels(newPanels);
                
                // Also update panelsMetadata
                const newMetadata = [...panelsMetadata];
                newMetadata[selectedPanelIndex] = {
                  ...newMetadata[selectedPanelIndex],
                  endTime: value
                };
                setPanelsMetadata(newMetadata);
                
                setHasChanges(true);
              }
            }}
            min={selectedPanel.startTime + 0.1}
            step={0.1}
            size="small"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div>
        <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
          Duration: {(selectedPanel.endTime - selectedPanel.startTime).toFixed(2)}s
        </Text>
      </div>
    </Space>
  );
};

export default TimingTabPanel;
